import { create } from "zustand";
import { State } from "@progress/kendo-data-query";
import { SampleListItemState } from "@presentation/common/types/extensions/SampleListItemState";
import { RawFile } from "@domain/schemas/BI";

interface InventoryStore {
  // the sample inventory data
  gridData: SampleListItemState[];
  setGridData: (gridData: SampleListItemState[]) => void;
  resetSelectedGridData: () => void;
  adjustSingleGridData: (gridData: SampleListItemState[], rawFileState: SampleListItemState) => void;
  adjustMultipleGridData: (gridData: SampleListItemState[], fileNames: SampleListItemState[], isSelected: boolean) => void;

  // filter of the GRID. Filter needs to include this CompositeFilterDescriptor
  metaState: State;
  setMetaState: (newMetaState: State) => void;

  // all filters for the API
  acquisitionDateUTC__lte: string | null;
  setAcquisitionDateUTC__lte: (acquisitionDateUTC__lte: string | null) => void;
  acquisitionDateUTC__gte: string | null;
  setAcquisitionDateUTC__gte: (acquisitionDateUTC__gte: string | null) => void;
  gxp: number | null;
  setGxp: (gxp: number | null) => void;

  // items for pagination
  pageSizeValue: number;
  setPageSizeValue: (pageSizeValue: number) => void;
  pageSkipValue: number;
  setPageSkipValue: (pageSkipValue: number) => void;
  pageTakeValue: number;
  setPageTakeValue: (pageTakeValue: number) => void;
}

// create the zustand store for the inventory
export const useInventoryStore = create<InventoryStore>()((set, get) => ({
  // functions to access and set GridData, as well as sample selection
  gridData: [],

  setGridData: (newGridData: RawFile[]) => {
    const currentGridData = get().gridData;
    const updatedGridData: SampleListItemState[] = newGridData.map((sample) => {
      const existingSample = currentGridData.find((s) => s.id === sample.id);
      return {
        ...sample,
        isSelected: existingSample ? existingSample.isSelected : false,
        isExpanded: existingSample ? existingSample.isExpanded : false,
        progress: existingSample ? existingSample.progress : 0,
        isFolder: Boolean((sample as { isFolder?: boolean }).isFolder),
      };
    });
    set({ gridData: updatedGridData });
  },

  // function will turn all 'selected' column items to false.
  resetSelectedGridData: () => {
    // get state
    const { gridData } = get();
    const newGridData = Array.isArray(gridData)
      ? gridData.map((element) => {
          element.isSelected = false;
          return element;
        })
      : [];
    // set both on GridData and selectedGridData
    set({ gridData: newGridData });
  },

  // function that will adjust a single row in gridData based on a provided rawFileState object.
  // Input will be such a rawFileState object, and a boolean value to set the selected value to.
  adjustSingleGridData: (gridData: SampleListItemState[], rawFileState: SampleListItemState) => {
    // find the element in the gridData and set the selected value to the selected value
    const newGridData = Array.isArray(gridData)
      ? gridData.map((element) => {
          if (element.fileName === rawFileState.fileName) {
            element.isSelected = !element.isSelected;
          }
          return element;
        })
      : [];
    set({ gridData: newGridData });
  },

  adjustMultipleGridData: (gridData: SampleListItemState[], selectedrawFileStates: SampleListItemState[], isSelected: boolean) => {
    // based on the fileNames, find the elements in the gridData and set the selected value to the selected value
    const newGridData = Array.isArray(gridData)
      ? gridData.map((element) => {
          if (selectedrawFileStates.map((rawFileState) => rawFileState.fileName).includes(element.fileName)) {
            element.isSelected = isSelected;
          }

          return element;
        })
      : [];
    set({ gridData: newGridData });
  },

  // always pre-set the sort by acquisitionDateUTC in descending order
  metaState: {},
  setMetaState: (newMetaState: State) => {
    set({ metaState: newMetaState });
    if (newMetaState.take) set({ pageSizeValue: newMetaState.take });
  },

  acquisitionDateUTC__lte: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).toISOString(),
  setAcquisitionDateUTC__lte: (acquisitionDateUTC__lte: string | null) => set({ acquisitionDateUTC__lte }),
  acquisitionDateUTC__gte: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 7).toISOString(),
  setAcquisitionDateUTC__gte: (acquisitionDateUTC__gte: string | null) => set({ acquisitionDateUTC__gte }),
  gxp: null,
  setGxp: (gxp: number | null) => set({ gxp }),

  pageSizeValue: 250,
  setPageSizeValue: (pageSizeValue: number) => set({ pageSizeValue }),
  pageSkipValue: 0,
  setPageSkipValue: (pageSkipValue: number) => set({ pageSkipValue }),
  pageTakeValue: 250,
  setPageTakeValue: (pageTakeValue: number) => set({ pageTakeValue }),
}));
